/**
 * ------------------------------------------------------------------------------------
 * This client was automatically generated. ** Do Not Edit **
 * ------------------------------------------------------------------------------------
 */
import { useCallback } from 'react';
import * as Rudder from '../rudder-web';
import { usePageActions } from '@cvent/sli-nextjs-metrics';
import { ApiCallback } from '@rudderstack/analytics-js';
import { TrackProperties } from '../commonTypes';

export type ActionDefinition = {
  id: string;
  destinations?: string[];
};

export interface EngagementMessageActivated extends TrackProperties {
  // unique id of the session.
  sessionId: string;
  // engagement panel feature name.
  feature: string;
  // action that triggers the activation of the engagement panel e.g. when QA is answered, when polling is opened, etc.
  messageTrigger: string;
  // the type of the engagement toast whether it's a badge or toast notification.
  messageType: string;
  // indicates whether the message was displayed in the panel or not.
  wasMessageDisplayed: boolean;
}
export interface EngagementToastMessageDismissed extends TrackProperties {
  // unique id of the session.
  sessionId: string;
  // engagement panel feature name.
  feature: string;
  // action that triggers the activation of the engagement panel e.g. when QA is answered, when polling is opened, etc.
  messageTrigger: string;
  // the type of the engagement toast whether it's a badge or toast notification.
  messageType: string;
  // indicates whether the message was displayed in the panel or not.
  wasMessageDisplayed: boolean;
}

interface AnalyticsApi {
  engagementMessageActivated: (
    props: EngagementMessageActivated,
    options?: Rudder.Options,
    callback?: ApiCallback
  ) => void;
  engagementToastMessageDismissed: (
    props: EngagementToastMessageDismissed,
    options?: Rudder.Options,
    callback?: ApiCallback
  ) => void;
}

/**
 * This hook exposes a typed api with functions from the application's tracking plan.
 * Internally, each function uses the usePageActions API to track actions and forward
 * them to the reporters that were installed during the Metrics initialization in your
 * application's main entry point component.
 *
 * Note: This is the recommended way of tracking/measuring actions as it allows you to
 * register base properties during the metrics' client initialization, which will be included
 * automatically to every action whenever they are executed by your instrumentation code
 *
 *
 * How to use it:
 *
 * `const { acqSignupCompleted } = useEngagementPageActionsApi();`
 *
 *  // Somewhere in your application you can use this function to track this action
 * `acqSignupCompleted({ userId, email, accountName });`
 *
 * // If you want to track this action as an error you can do it with this piece of code below
 *  `acqSignupCompleted({ userId, email, error: '500 Error........' });`
 *
 * // If you want to track this action as an expected error you can do it with this piece of code below
 *  `acqSignupCompleted({ userId, email, expected_error: 'This email address is already associated with an existing account.' });`
 *
 * @returns AnalyticsApi
 */
export const useEngagementPageActionsApi = (): AnalyticsApi => {
  const { trackAction, getBaseProps } = usePageActions();
  const { trackingPlanName } = getBaseProps() || {};

  /**
   * this action tracks the users who receive engagement toast notification when certain engagement feature is opened/enabled in order to increase engagement and use of the engagement features in the session.
   *
   * Action Definition:
   * ------------------------------------------------------
   * status        : active
   * id            :
   * name          : attendee hub Engagement Message Activated
   * downstreamName: Engagement Message Activated
   * owner         : Raptor
   * externalName  : Engagement Message Activated
   * phase         : engagement
   * release       : v1.0
   * version       :
   * type          : track
   * multiStep     :  no
   * trigger       : this action is triggered when certain engagement features are opened/enabled, for example, when a live polling is opened, etc.
   * destinations  : Mixpanel,Analytics
   * sdk           : analytics.js
   * ------------------------------------------------------
   *
   * @param {EngagementMessageActivated} props - The analytics properties that will be sent to RudderStack.
   * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
   */
  const engagementMessageActivated = useCallback(
    (props: EngagementMessageActivated, options?: Rudder.Options): void => {
      // Grab the destinations from the definition so the rudderstack reporter configures
      // the integrations accordingly
      const destinations = 'Mixpanel,Analytics'.split(',');
      // RudderStack unique action name for the workspace
      const action = {
        id: 'attendee hub Engagement Message Activated',
        ...(destinations && { destinations })
      };
      const metadata = JSON.parse(
        '{"analytics":{"source":"Attendee Hub Web","id":"engagement_message_activated","name":"Engagement Message Activated","type":"Action"}}'
      );
      const hasMetadata = Object.keys(metadata).length > 0;
      // Track action via the pageActions API
      trackAction(
        action,
        {
          ...props,
          ...getBaseProps(),
          version: '',
          downstreamName: 'Engagement Message Activated'
        },
        {
          ...options,
          trackingPlanName,
          status: 'active',
          metadata: hasMetadata ? metadata : undefined
        }
      );
    },
    [getBaseProps, trackAction]
  );
  /**
   * this action tracks the users who closes the engagement toast notification in order to track how many people dismiss the engagement toast.
   *
   * Action Definition:
   * ------------------------------------------------------
   * status        : active
   * id            :
   * name          : attendee hub Engagement Toast Message Dismissed
   * downstreamName: Engagement Toast Message Dismissed
   * owner         : Raptor
   * externalName  : Engagement Toast Message Dismissed
   * phase         : engagement
   * release       : v1.0
   * version       :
   * type          : track
   * multiStep     :  no
   * trigger       : this action is triggered when attendee clicks on the close/dismiss button on the engagement toast.
   * destinations  : Mixpanel,Analytics
   * sdk           : analytics.js
   * ------------------------------------------------------
   *
   * @param {EngagementToastMessageDismissed} props - The analytics properties that will be sent to RudderStack.
   * @param {Object} [options] - A dictionary of options. For example, enable or disable specific destinations for the call.
   */
  const engagementToastMessageDismissed = useCallback(
    (props: EngagementToastMessageDismissed, options?: Rudder.Options): void => {
      // Grab the destinations from the definition so the rudderstack reporter configures
      // the integrations accordingly
      const destinations = 'Mixpanel,Analytics'.split(',');
      // RudderStack unique action name for the workspace
      const action = {
        id: 'attendee hub Engagement Toast Message Dismissed',
        ...(destinations && { destinations })
      };
      const metadata = JSON.parse(
        '{"analytics":{"source":"Attendee Hub Web","id":"engagement_toast_message_dismissed","name":"Engagement Toast Message Dismissed","type":"Action"}}'
      );
      const hasMetadata = Object.keys(metadata).length > 0;
      // Track action via the pageActions API
      trackAction(
        action,
        {
          ...props,
          ...getBaseProps(),
          version: '',
          downstreamName: 'Engagement Toast Message Dismissed'
        },
        {
          ...options,
          trackingPlanName,
          status: 'active',
          metadata: hasMetadata ? metadata : undefined
        }
      );
    },
    [getBaseProps, trackAction]
  );

  return {
    engagementMessageActivated,
    engagementToastMessageDismissed
  };
};
